<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import { API } from '@/store/urls';

const b = bem('add-page-modal');

export default {
  name: 'AddPageModal',
  components: {
    PencilIcon,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    pageKey: {
      type: String,
      default: '',
    },
    editingPage: {
      type: Object,
      default: null,
    },
    existingPages: {
      type: Array,
      default: () => [],
    },
    // WHY: When true, this is the first page for the company — URL is forced to root
    // and cannot be changed. Driven by backend home page logic.
    isFirstPage: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'save'],
  data() {
    return {
      pageSlug: '',
      pageName: '',
      isShowHeader: false,
      titleSeo: '',
      descriptionSeo: '',
      keywordsSeo: '',
    };
  },
  computed: {
    isEditMode() {
      return !!this.editingPage;
    },
    isSlugDuplicate() {
      const currentSlug = this.pageSlug.trim().toLowerCase();
      return this.existingPages.some((page) => {
        if (this.isEditMode && page.id === this.editingPage.id) return false;
        const pageSlug = this.extractSlugFromUrl(page.url);
        return pageSlug.toLowerCase() === currentSlug;
      });
    },
    isNameDuplicate() {
      const currentName = this.pageName.trim().toLowerCase();
      if (!currentName) return false;
      return this.existingPages.some((page) => {
        if (this.isEditMode && page.id === this.editingPage.id) return false;
        return page.name?.toLowerCase() === currentName;
      });
    },
    // WHY: URL is locked for home pages (first page or editing existing home page).
    // Home page always has the root URL which cannot be changed.
    isUrlLocked() {
      if (this.isFirstPage) return true;
      if (this.isEditMode && this.editingPage?.isHomePage) return true;
      return false;
    },
    isReservedSlug() {
      const reserved = ['gallery', 'product-gallery', 'photos'];
      return reserved.includes(this.pageSlug.trim().toLowerCase());
    },
    isSaveDisabled() {
      return !this.pageName.trim() || this.isSlugDuplicate
        || this.isNameDuplicate || this.isReservedSlug;
    },
    // URL for saving to server (relative path without domain)
    relativeUrl() {
      const slug = this.pageSlug ? `/${this.pageSlug}` : '';
      return `pod/${this.pageKey}${slug}`;
    },
    // Base URL for display in UI (prefix shown before slug input)
    // Uses API base URL with subdomains removed (api.example.com -> example.com)
    // For dev (with port) replaces port with 3000, for prod (no port) keeps as is
    displayBaseUrl() {
      try {
        const url = new URL(API);
        const { hostname } = url;
        // Check if hostname is an IP address (don't modify IPs)
        const isIP = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
        let mainDomain = hostname;
        if (!isIP) {
          // Remove subdomains: keep only the last two parts (domain + TLD)
          const hostParts = hostname.split('.');
          mainDomain = hostParts.length > 2
            ? hostParts.slice(-2).join('.')
            : hostname;
        }
        // If port exists (dev environment), replace with 3000
        const portPart = url.port ? ':3000' : '';
        return `${url.protocol}//${mainDomain}${portPart}/pod/${this.pageKey}/`;
      } catch {
        // Fallback if URL parsing fails
        return `https://foreverstudios.com/pod/${this.pageKey}/`;
      }
    },
    // Full URL for opening in new tab (prefix + slug)
    fullPageUrl() {
      return `${this.displayBaseUrl}${this.pageSlug}`;
    },
    modalTitle() {
      return this.isEditMode ? 'Edit Page' : 'Please Create a URL for This Page';
    },
    buttonText() {
      return this.isEditMode ? 'SAVE CHANGES' : 'CREATE & SAVE';
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        if (this.editingPage) {
          const slug = this.extractSlugFromUrl(this.editingPage.url);
          this.pageSlug = slug;
          this.pageName = this.editingPage.name || '';
          this.isShowHeader = this.editingPage.showInNav || false;
          this.titleSeo = this.editingPage.seoTitle || '';
          this.descriptionSeo = this.editingPage.seoDescription || '';
          this.keywordsSeo = this.editingPage.seoKeywords || '';
        } else {
          this.pageSlug = '';
          // WHY: Auto-fill "Home" for first page to guide the admin
          this.pageName = this.isFirstPage ? 'Home' : '';
          this.isShowHeader = false;
          this.titleSeo = '';
          this.descriptionSeo = '';
          this.keywordsSeo = '';
        }
      }
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    extractSlugFromUrl(url) {
      if (!url) return '';
      // Extract slug from URL like "https://domain.com/pod/pageKey/slug" or "pod/pageKey/slug"
      // Handle both absolute URLs and relative paths
      const match = url.match(/\/?pod\/[^/]+(?:\/(.*))?$/);
      if (match) {
        // Remove trailing slash if present
        return (match[1] || '').replace(/\/$/, '');
      }
      return '';
    },
    handleSave() {
      if (!this.isSaveDisabled) {
        const data = {
          slug: this.pageSlug,
          name: this.pageName,
          url: this.relativeUrl,
          showInNav: this.isShowHeader,
          seoTitle: this.titleSeo,
          seoDescription: this.descriptionSeo,
          seoKeywords: this.keywordsSeo,
        };

        if (this.isEditMode) {
          data.id = this.editingPage.id;
          data.footerOrder = this.editingPage.footerOrder;
          data.isPublished = this.editingPage.isPublished;
          data.publishedAt = this.editingPage.publishedAt;
        }

        this.$emit('save', data);
      }
    },
    handleCancel() {
      this.$emit('close');
    },
    openPageUrl() {
      window.open(this.fullPageUrl, '_blank');
    },
  },
};
</script>

<template>
  <a-modal
    :open="visible"
    :footer="null"
    :closable="true"
    :width="800"
    centered
    @cancel="handleCancel"
    :class="b()"
  >
    <div :class="b('content')">
      <h2 :class="b('title')">{{ modalTitle }}</h2>
      <div :class="b('separate')" />

      <div :class="b('form')">
        <div :class="b('field')">
          <span
            :class="b('label', { link: true })"
            role="button"
            tabindex="0"
            @click="openPageUrl"
            @keydown.enter="openPageUrl"
          >
            URL
            <svg
              :class="b('link-icon')"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11 7.66667V11.6667C11 12.0203 10.8595 12.3594
                   10.6095 12.6095C10.3594 12.8595 10.0203 13 9.66667 13H2.33333C1.97971
                   13 1.64057 12.8595 1.39052 12.6095C1.14048 12.3594 1 12.0203 1
                   11.6667V4.33333C1 3.97971 1.14048 3.64057 1.39052 3.39052C1.64057
                   3.14048 1.97971 3 2.33333 3H6.33333"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M9 1H13V5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5.66667 8.33333L13 1"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <div :class="b('url-input-wrapper', {
            error: isSlugDuplicate || isReservedSlug,
            locked: isUrlLocked,
          })">
            <span :class="b('url-prefix')">{{ displayBaseUrl }}</span>
            <div :class="b('input-with-placeholder')">
              <input
                :class="b('url-input')"
                type="text"
                aria-label="Page URL slug"
                v-model="pageSlug"
                :disabled="isUrlLocked"
              />
              <span v-if="!pageSlug && !isUrlLocked" :class="b('placeholder')">
                <PencilIcon :size="16" />
              </span>
            </div>
          </div>
          <span v-if="isUrlLocked" :class="b('hint')">
            Home Page — URL cannot be changed
          </span>
          <span v-else-if="isSlugDuplicate" :class="b('error')">
            This URL already exists
          </span>
          <span v-else-if="isReservedSlug" :class="b('error')">
            This URL is reserved and cannot be used
          </span>
        </div>
        <div :class="b('field')">
          <span :class="b('label')">
            Page Name (will show on Header (optional), Footer & Admin)
          </span>
          <div :class="b('input-wrapper', { error: isNameDuplicate })">
            <div :class="b('input-with-placeholder')">
              <input
                :class="b('input', { error: isNameDuplicate })"
                type="text"
                aria-label="Page name"
                maxlength="12"
                v-model="pageName"
              />
              <span v-if="!pageName" :class="b('placeholder')">
                Page Name <PencilIcon :size="16" />
              </span>
            </div>
            <span :class="b('char-counter')">{{ pageName.length }}/12</span>
          </div>
          <span v-if="isNameDuplicate" :class="b('error')">This page name already exists</span>
        </div>
        <div :class="b('toggle-row')">
          <button
            type="button"
            role="switch"
            :aria-checked="isShowHeader"
            :class="b('toggle', { active: isShowHeader })"
            @click="isShowHeader = !isShowHeader"
          >
            <span :class="b('toggle-ball', { active: isShowHeader })" />
          </button>
          <span :class="b('toggle-label')">Show on header</span>
        </div>
        <div :class="b('field')">
          <span :class="b('label')">SEO Title</span>
          <div :class="b('input-wrapper')">
            <div :class="b('input-with-placeholder')">
              <input
                :class="b('input')"
                type="text"
                aria-label="SEO Title"
                v-model="titleSeo"
              />
              <span v-if="!titleSeo" :class="b('placeholder')">
                SEO Title <PencilIcon :size="16" />
              </span>
            </div>
          </div>
        </div>
        <div :class="b('field')">
          <span :class="b('label')">SEO Description</span>
          <div :class="b('input-wrapper')">
            <div :class="b('input-with-placeholder')">
              <textarea
                :class="b('input', { textarea: true })"
                aria-label="SEO Description"
                v-model="descriptionSeo"
              />
              <span v-if="!descriptionSeo" :class="b('placeholder', { textarea: true })">
                SEO Description <PencilIcon :size="16" />
              </span>
            </div>
          </div>
        </div>

        <div :class="b('field')">
          <span :class="b('label')">SEO Keywords</span>
          <div :class="b('input-wrapper')">
            <div :class="b('input-with-placeholder')">
              <input
                :class="b('input')"
                type="text"
                aria-label="SEO Keywords"
                v-model="keywordsSeo"
              />
              <span v-if="!keywordsSeo" :class="b('placeholder')">
                SEO Keywords <PencilIcon :size="16" />
              </span>
            </div>
          </div>
        </div>
      </div>

      <div :class="b('buttons')">
        <button
          :class="b('btn', { save: true, disabled: isSaveDisabled })"
          :disabled="isSaveDisabled"
          @click="handleSave"
        >
          {{ buttonText }}
        </button>
      </div>
    </div>
  </a-modal>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.add-page-modal {
  &__content {
    padding: 0 16px;
    @include mediaMinMax(1025, $normal) {
      padding: 0 vw-full(16);
    }
  }

  &__separate {
    width: 100%;
    height: 6px;
    background: #DDD;
    margin: 28px 0;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(6);
      margin: vw-full(28) 0;
    }
  }

  &__title {
    font-family: Montserrat, sans-serif;
    font-size: 28px;
    font-weight: 500;
    color: #293241;
    text-align: center;
    margin-bottom: 0;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(28);
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 32px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(20);
      margin-bottom: vw-full(32);
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(4);
    }
  }

  &__label {
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #293241;
    text-align: left;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
    }

    &_link {
      display: flex;
      align-items: center;
      gap: 6px;
      cursor: pointer;
      transition: color 0.2s ease;
      font-size: 14px;

      @include mediaMinMax(1025, $normal) {
        gap: vw-full(6);
        font-size: vw-full(14);
      }

      &:hover {
        color: #0066cc;
      }
    }
  }

  &__link-icon {
    flex-shrink: 0;
    width: 14px;
    height: 14px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(14);
      height: vw-full(14);
    }
  }

  &__url-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1;
    width: 720px;
    height: 48px;
    border: 1px solid #ddd;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(720);
      height: vw-full(48);
      border-radius: vw-full(8);
    }

    &_error {
      border-color: #dc3545;
    }

    &_locked {
      background: #f5f5f5;
      opacity: 0.8;
    }
  }

  &__url-prefix {
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #7f7f7f;
    padding-left: 16px;
    white-space: nowrap;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
      padding-left: vw-full(16);
    }
  }

  &__url-input {
    flex: 1;
    height: 48px;
    border: none;
    outline: none;
    font-family: Montserrat, sans-serif;
    font-size: 18px;
    font-weight: 400;
    color: #293241;
    padding: 0 16px 0 0;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(48);
      font-size: vw-full(18);
      padding: 0 vw-full(16) 0 0;
    }

    &::placeholder {
      color: #999;
    }
  }

  &__input {
    flex: 1;
    width: 720px;
    height: 48px;
    border: 1px solid #ddd;
    border-radius: 12px;
    outline: none;
    font-family: Montserrat, sans-serif;
    font-size: 18px;
    font-weight: 400;
    color: #293241;
    padding: 14px 20px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(720);
      height: vw-full(48);
      font-size: vw-full(18);
      padding: vw-full(14) vw-full(20);
      border-radius: vw-full(12);
    }

    &::placeholder {
      color: #999;
    }

    &:focus {
      border-color: #242424;
    }
    &_textarea {
      min-height: 100px;
      @include mediaMinMax(1025, $normal) {
        min-height: vw-full(100);
      }
    }
    &_error {
      border-color: #dc3545;
    }
  }

  &__error {
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #dc3545;
    margin-top: 4px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
      margin-top: vw-full(4);
    }
  }

  &__hint {
    font-family: Montserrat, sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #7f7f7f;
    margin-top: 4px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(12);
      margin-top: vw-full(4);
    }
  }

  &__input-wrapper {
    position: relative;
    width: 720px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(720);
    }
  }

  &__char-counter {
    position: absolute;
    right: 4px;
    bottom: 4px;
    font-family: Montserrat, sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #7f7f7f;

    @include mediaMinMax(1025, $normal) {
      right: vw-full(4);
      bottom: vw-full(4);
      font-size: vw-full(12);
    }
  }

  &__input-with-placeholder {
    position: relative;
    flex: 1;
    width: 100%;
  }

  &__placeholder {
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: Montserrat, sans-serif;
    font-size: 18px;
    font-weight: 400;
    color: #999;
    pointer-events: none;

    @include mediaMinMax(1025, $normal) {
      left: vw-full(20);
      gap: vw-full(6);
      font-size: vw-full(18);
    }

    &_textarea {
      top: 14px;
      transform: none;

      @include mediaMinMax(1025, $normal) {
        top: vw-full(14);
      }
    }
  }

  &__toggle-row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    margin-top: -8px;
    width: 100%;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
      margin-top: vw-full(-8);
    }
  }

  &__toggle {
    width: 56px;
    height: 28px;
    border-radius: 14px;
    border: 2px solid #242424;
    background: #fff;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
    padding: 0;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(56);
      height: vw-full(28);
      border-radius: vw-full(14);
      border-width: vw-full(2);
    }
  }

  &__toggle-ball {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #242424;
    transition: all 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      left: vw-full(2);
      width: vw-full(20);
      height: vw-full(20);
    }

    &_active {
      left: calc(100% - 22px);

      @include mediaMinMax(1025, $normal) {
        left: calc(100% - #{vw-full(22)});
      }
    }
  }

  &__toggle-label {
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #242424;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
    }
  }

  &__buttons {
    display: flex;
    justify-content: center;
    gap: 16px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
    }
  }

  &__btn {
    min-width: 140px;
    height: 48px;
    padding: 0 32px;
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 500;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      min-width: vw-full(140);
      height: vw-full(48);
      padding: 0 vw-full(32);
      font-size: vw-full(16);
      border-radius: vw-full(12);
    }

    &_save {
      background: #242424;
      color: #fff;
      border: 1px solid #242424;

      &:hover {
        background: #333;
      }
    }

    &_disabled {
        background: #fff;
        color: #242424;
        border: 1px solid #242424;

        &:hover {
          background: #f5f5f5;
        }
      }
  }
}
</style>
